import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  makeStyles,
} from '@material-ui/core';
import { Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import esLocale from 'date-fns/locale/es';

import Request from '../../../requests/api/successStory';
import ClusterRequest from '../../../requests/api/cluster';
import slugify from '../../../utils/slug';
import { CASO_EXITO } from '../../../utils/const/clusterTypes';
import { SUCCESS_STORY } from '../../../utils/const/contentTypes';
import ImageUploadField from '../../../components/ImageUploadField';
import RichTextEditor from '../../../components/RichTextEditor';
import SeoFields from '../../../components/SeoFields';
import { SUPER_ADMIN } from '../../../utils/const/user_types';
import {
  SUCCESS_STORY_COVERS,
  SUCCESS_STORY_OG,
  SUCCESS_STORY_CONTENT,
} from '../../../utils/const/uploadFolders';
import {
  SEO_TITLE_MAX_LENGTH,
  OG_TITLE_MAX_LENGTH,
} from '../../../utils/const/seoLimits';

const useStyles = makeStyles(() => ({
  root: {},
}));

function EditForm({
  className,
  item,
  ...rest
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [published, setPublished] = useState(item.published);
  const [content, setContent] = useState(item.content || '');
  const [clusters, setClusters] = useState([]);

  const account = useSelector((state) => state.account);

  useEffect(() => {
    const fetchClusters = async () => {
      const response = await ClusterRequest.getClusters(CASO_EXITO);
      if (response.data?.data) {
        setClusters(response.data.data);
      }
    };
    fetchClusters();
  }, []);

  if (submitSuccess) {
    return <Redirect to={`/administracion/blog?tab=${SUCCESS_STORY}`} />;
  }

  return (
    <Formik
      initialValues={{
        userId: item.userId || '',
        clusterId: item.clusterId || '',
        title: item.title || '',
        excerpt: item.excerpt || '',
        slug: item.slug || '',
        coverImage: item.coverImage || '',
        seoTitle: item.seoTitle || '',
        metaDescription: item.metaDescription || '',
        primaryKeyword: item.primaryKeyword || '',
        secondaryKeywords: (item.secondaryKeywords || []).join(', '),
        ogTitle: item.ogTitle || '',
        ogImage: item.ogImage || '',
        publishedDate: (item.publishedDate && new Date(item.publishedDate))
          || (item.createdAt && new Date(item.createdAt))
          || new Date(),
        published,
      }}
      validationSchema={Yup.object().shape({
        clusterId: Yup.number().required('Selecciona un cluster'),
        title: Yup.string().max(255).required('Debe ingresar un título'),
        excerpt: Yup.string().required('Debe ingresar un extracto'),
        slug: Yup.string().max(255).required('Debe ingresar un slug'),
        metaDescription: Yup.string().max(160).required('Debe ingresar una meta descripción'),
        primaryKeyword: Yup.string().max(100).required('Debe ingresar la keyword principal'),
        seoTitle: Yup.string().max(SEO_TITLE_MAX_LENGTH),
        ogTitle: Yup.string().max(OG_TITLE_MAX_LENGTH),
        publishedDate: Yup.date().typeError('Fecha inválida').required('Debe ingresar una fecha de publicación'),
      })}
      onSubmit={async (values, {
        setErrors,
        setStatus,
        setSubmitting,
      }) => {
        try {
          const data = {
            id: item.id,
            userId: values.userId,
            clusterId: values.clusterId,
            title: values.title,
            excerpt: values.excerpt,
            slug: values.slug,
            content,
            coverImage: values.coverImage,
            seoTitle: values.seoTitle,
            metaDescription: values.metaDescription,
            primaryKeyword: values.primaryKeyword,
            secondaryKeywords: values.secondaryKeywords
              ? values.secondaryKeywords.split(',').map((k) => k.trim()).filter(Boolean)
              : [],
            ogTitle: values.ogTitle,
            ogImage: values.ogImage || values.coverImage,
            publishedDate: values.publishedDate,
            published,
          };
          const response = await Request.putSuccessStoryEdit({ id: item.id, successStory: data });

          const res = response.data;
          if (res.success) {
            setStatus({ success: true });
            setSubmitting(false);
            setSubmitSuccess(true);
            enqueueSnackbar('Cambios guardados', {
              variant: 'success',
            });
          } else {
            setStatus({ success: false });
            setErrors({ submit: res.data.errorMessage });
            enqueueSnackbar(res.data.errorMessage, {
              variant: 'warning',
            });
          }
        } catch (error) {
          setStatus({ success: false });
          setErrors({ submit: error.message });
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        touched,
        values,
      }) => (
        <form
          className={clsx(classes.root, className)}
          onSubmit={handleSubmit}
          {...rest}
        >
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  <TextField
                    error={Boolean(touched.title && errors.title)}
                    fullWidth
                    helperText={touched.title && errors.title}
                    label="Título"
                    name="title"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    value={values.title}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    error={Boolean(touched.excerpt && errors.excerpt)}
                    fullWidth
                    helperText={touched.excerpt && errors.excerpt}
                    label="Extracto"
                    name="excerpt"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    value={values.excerpt}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    error={Boolean(touched.slug && errors.slug)}
                    fullWidth
                    helperText={touched.slug && errors.slug}
                    label="Slug"
                    name="slug"
                    onBlur={handleBlur}
                    onChange={(event) => setFieldValue('slug', slugify(event.target.value))}
                    required
                    value={values.slug}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    error={Boolean(touched.clusterId && errors.clusterId)}
                    fullWidth
                    helperText={touched.clusterId && errors.clusterId}
                    label="Cluster"
                    name="clusterId"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    select
                    value={values.clusterId}
                    variant="outlined"
                  >
                    {clusters.map((cluster) => (
                      <MenuItem key={cluster.id} value={cluster.id}>
                        {cluster.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item md={6} xs={12}>
                  <ImageUploadField
                    error={Boolean(touched.coverImage && errors.coverImage)}
                    folder={SUCCESS_STORY_COVERS}
                    helperText={touched.coverImage && errors.coverImage}
                    label="Imagen de portada"
                    name="coverImage"
                    onBlur={handleBlur}
                    onChange={(url) => setFieldValue('coverImage', url)}
                    required
                    value={values.coverImage}
                  />
                </Grid>
                <Grid item xs={12}>
                  <RichTextEditor
                    value={content}
                    onChange={setContent}
                    folder={SUCCESS_STORY_CONTENT}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    disabled
                    fullWidth
                    label="Fecha de creación"
                    value={item.createdAt ? format(new Date(item.createdAt), 'dd-MM-yyyy') : 'Sin registro'}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={esLocale}
                  >
                    <DatePicker
                      inputFormat="dd-MM-yyyy"
                      views={['year', 'month', 'day']}
                      label="Fecha de publicación"
                      value={values.publishedDate}
                      onChange={(value) => setFieldValue('publishedDate', value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="outlined"
                          error={Boolean(touched.publishedDate && errors.publishedDate)}
                          helperText={(touched.publishedDate && errors.publishedDate)
                            || 'Se edita manualmente, no cambia al publicar.'}
                          onBlur={handleBlur}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <SeoFields
                  errors={errors}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  ogImageFolder={SUCCESS_STORY_OG}
                  setFieldValue={setFieldValue}
                  touched={touched}
                  values={values}
                />
                {account.user.role.name === SUPER_ADMIN
                  ? (
                    <Grid item md={6} xs={12}>
                      <FormControl component="fieldset" variant="standard">
                        <FormLabel component="legend">Publicar</FormLabel>
                        <Switch
                          checked={published}
                          onChange={() => setPublished(!published)}
                          inputProps={{ 'aria-label': 'controlled' }}
                        />
                      </FormControl>
                    </Grid>
                  ) : null}
              </Grid>
              <Box mt={2}>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  Guardar
                </Button>
              </Box>
            </CardContent>
          </Card>
        </form>
      )}
    </Formik>
  );
}

EditForm.propTypes = {
  className: PropTypes.string,
  item: PropTypes.object.isRequired,
};

export default EditForm;
